import { useCallback, useEffect, useState } from 'react';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useTransactionControl, supportsTransactions } from './useTransactionControl';
import { rollbackTransaction, releaseTransaction } from '../services/transactionApi';
import {
  announceConfirmedTransactionOutcome,
  claimTransactionLostFallback,
  TRANSACTION_LOST_DESCRIPTION,
  TRANSACTION_TAB_IDLE_RESET,
} from './useTransactionSessionsReleased';
import { toast } from '../../../../components/ui/toaster';
import type { TransactionMode, TransactionState, TransactionIsolationLevel } from '../../core/types/workspaceTypes';
import type { DatabaseEngineId } from '../../../../core/engines';

type UseTabTransactionsParams = {
  tabId: string;
  transactionMode: TransactionMode;
  transactionState: TransactionState;
  /** The session the tab began (`QueryTabContent.transactionSessionGeneration`). */
  transactionSessionGeneration: number | undefined;
  /** A BEGIN is on the wire (`QueryTabContent.transactionBeginToken` is set). */
  transactionBeginPending: boolean;
  isolationLevel: TransactionIsolationLevel | undefined;
  connectionId: string | undefined;
  database: string | undefined;
  engineId: DatabaseEngineId;
  isDisconnected: boolean;
  isActiveTab: boolean;
};

/**
 * Transaction wiring for a query editor tab — the toolbar's transaction prop
 * block (mode / isolation / commit / rollback + enablement), the
 * connection-drop transaction release, `prepareForExecution` (auto-begin), and
 * the transaction-guarded database-switch confirmation. Behavior carried
 * verbatim from the previous inline implementation.
 */
export function useTabTransactions({
  tabId,
  transactionMode,
  transactionState,
  transactionSessionGeneration,
  transactionBeginPending,
  isolationLevel,
  connectionId,
  database,
  engineId,
  isDisconnected,
  isActiveTab,
}: UseTabTransactionsParams) {
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);
  const updateTabConnectionInfo = useWorkspaceStore((state) => state.updateTabConnectionInfo);

  // Connection drop detection — the FALLBACK for a network drop the backend
  // never announces. A teardown the backend does announce
  // (`transaction:session-released`) is handled by
  // `useTransactionSessionsReleased`; whichever of the two acts first claims
  // this tab's session and the other stays silent, so a Reconnect never shows
  // "Transaction lost" and "Transaction ended by reconnect" back to back
  // (review 2026-09-05, P1).
  useEffect(() => {
    if (!isDisconnected) return;
    const generation = transactionSessionGeneration;
    const claimed = claimTransactionLostFallback(tabId, {
      transactionState,
      transactionSessionGeneration: generation,
    });
    if (!claimed) return;
    // Same end state as the announced release (`TRANSACTION_TAB_IDLE_RESET`):
    // the session is released right here, so the tab has nothing left to settle.
    updateTabContent(tabId, TRANSACTION_TAB_IDLE_RESET);
    // Nothing is confirmed yet (final review, MAJOR): the release below is
    // what can say whether a ROLLBACK was accepted, and its answer — or the
    // teardown's own event — upgrades this toast once it arrives.
    toast.warning({ title: 'Transaction lost', description: TRANSACTION_LOST_DESCRIPTION });
    void releaseTransaction(tabId).then((result) => {
      if (result.outcome) announceConfirmedTransactionOutcome(tabId, generation, result.outcome, 'disconnect');
    });
  }, [isDisconnected, transactionState, transactionSessionGeneration, tabId, updateTabContent]);

  // Transaction controls
  const {
    showCommitRollback: _showCommitRollback,
    commitEnabled: transactionCommitEnabled,
    rollbackEnabled: transactionRollbackEnabled,
    modeChangeDisabled: transactionModeChangeDisabled,
    isolationLevelState: transactionIsolationLevelState,
    setTransactionMode: handleTransactionModeChange,
    setIsolationLevel: handleTransactionIsolationLevelChange,
    handleCommit: handleTransactionCommit,
    handleRollback: handleTransactionRollback,
    prepareForExecution,
  } = useTransactionControl({
    tabId,
    transactionMode,
    transactionState,
    transactionSessionGeneration,
    transactionBeginPending,
    isolationLevel,
    connectionId,
    database,
    engineId,
    disabled: !connectionId || isDisconnected,
    isActiveTab,
  });
  void _showCommitRollback; // derived in toolbar from mode

  // Hide the transaction controls entirely for engines without transaction
  // support (e.g. kusto/KQL). Passing `transactionMode={undefined}` collapses
  // the toolbar's transaction block and its two-row layout.
  const showTransactionControls = supportsTransactions(engineId);

  // Database switch with active transaction warning
  const [pendingDatabaseChange, setPendingDatabaseChange] = useState<string | null>(null);

  const handleDatabaseChange = useCallback((newDatabase: string) => {
    if (transactionState === 'active' || transactionState === 'error') {
      setPendingDatabaseChange(newDatabase);
      return;
    }
    updateTabConnectionInfo(tabId, { database: newDatabase || undefined });
  }, [tabId, updateTabConnectionInfo, transactionState]);

  const handleConfirmDatabaseChange = useCallback(async () => {
    if (pendingDatabaseChange === null) return;
    await rollbackTransaction(tabId);
    await releaseTransaction(tabId);
    updateTabContent(tabId, TRANSACTION_TAB_IDLE_RESET);
    updateTabConnectionInfo(tabId, { database: pendingDatabaseChange || undefined });
    setPendingDatabaseChange(null);
  }, [pendingDatabaseChange, tabId, updateTabContent, updateTabConnectionInfo]);

  return {
    transactionCommitEnabled,
    transactionRollbackEnabled,
    transactionModeChangeDisabled,
    transactionIsolationLevelState,
    handleTransactionModeChange,
    handleTransactionIsolationLevelChange,
    handleTransactionCommit,
    handleTransactionRollback,
    prepareForExecution,
    showTransactionControls,
    pendingDatabaseChange,
    setPendingDatabaseChange,
    handleDatabaseChange,
    handleConfirmDatabaseChange,
  };
}
